import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useModal } from 'hooks';
import {
  showErrorMessage,
  showSuccessMessage,
  getErrorText,
  buildAssetsUrl,
  buildIconAssetsUrl,
} from 'helpers';
import imageCompression from 'browser-image-compression';
import {
  deleteGlobalAsset,
  deleteAsset,
  uploadAssets,
  clearTemplateImages,
  clearGlobalImages,
  getAssets,
} from 'store/actions';
import {
  globalImageSelector,
  templateIconSelector,
  templateImageSelector,
} from 'store/selectors';

import { validateFile, validateTotalFileSize } from 'utils/fileValidation';

export const useTemplateImages = (id, withIconsTab = false) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const {
    isOpen: isConfirmTemplateDeleteModalOpen,
    open: openConfirmTemplateDeleteModal,
    close: closeConfirmTemplateDeleteModal,
  } = useModal();

  const {
    isOpen: isConfirmGlobalDeleteModalOpen,
    open: openConfirmGlobalDeleteModal,
    close: closeConfirmGlobalDeleteModal,
  } = useModal();

  const {
    isOpen: isUploadFolderModalOpen,
    open: openUploadFolderModal,
    close: closeUploadFolderModal,
  } = useModal();

  const [isTemplateImageDeleteLoading, setIsTemplateImageDeleteLoading] =
    useState(false);
  const [isGlobalImageDeleteLoading, setIsGlobalImageDeleteLoading] =
    useState(false);
  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadType, setUploadType] = useState('image');

  const templateImagesState = useSelector(templateImageSelector);
  const templateIconsState = useSelector(templateIconSelector);
  const globalImagesState = useSelector(globalImageSelector);

  useEffect(() => {
    dispatch(getAssets({ id, type: withIconsTab ? 'icon' : 'image' }));

    // clearing assets on modal unmount
    return () => {
      dispatch(clearGlobalImages());
      dispatch(clearTemplateImages());
    };
  }, [id, withIconsTab, dispatch]);

  const handleUploadImage = async (
    files,
    rejectedFiles = [],
    folderId = null,
    type = 'image',
    skipFolderModal = false,
  ) => {
    const validFiles = [];

    setUploadType(type);

    // 1. Handle browser/dropzone level rejections (e.g. if we had 'accept' or size limits in dropzone)
    if (rejectedFiles.length > 0) {
      rejectedFiles.forEach(({ file, errors }) => {
        showErrorMessage(`${file.name}: ${errors[0].message}`);
      });
    }

    // 2. Security Validation for accepted files
    for (const file of files) {
      const { isValid, error } = await validateFile(file, type);
      if (!isValid) {
        showErrorMessage(error);
      } else {
        validFiles.push(file);
      }
    }

    if (validFiles.length === 0) {
      return;
    }

    // 3. Combined size validation across all accepted files
    const totalSizeResult = validateTotalFileSize(validFiles);
    if (!totalSizeResult.isValid) {
      showErrorMessage(totalSizeResult.error);
      return;
    }

    if (folderId || skipFolderModal) {
      await handleUploadWithFolder(folderId, validFiles, type);
    } else {
      setPendingFiles(validFiles);
      openUploadFolderModal();
    }
  };

  const handleUploadWithFolder = async (
    folderId,
    files = pendingFiles,
    type = uploadType,
  ) => {
    if (files.length === 0) {
      return;
    }

    setIsUploadLoading(true);

    const compressOptions = {
      maxSizeMB: 10,
      maxWidthOrHeight: 4096,
      useWebWorker: true,
    };

    const generateUuidFileName = (originalName) => {
      const extension = originalName.split('.').pop();
      const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
        /[xy]/g,
        function (c) {
          var r = (Math.random() * 16) | 0,
            v = c === 'x' ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        },
      );
      return `${uuid}.${extension}`;
    };

    const mimeToExtension = {
      'image/jpeg': 'jpg',
      'image/png': 'png',
    };

    const compressAllFiles = async (file) => {
      const isSvg =
        file.type === 'image/svg+xml' ||
        file.name.toLowerCase().endsWith('.svg');
      const isGif =
        file.type === 'image/gif' || file.name.toLowerCase().endsWith('.gif');

      try {
        if (isSvg || isGif) {
          // SVG is text/XML-based and GIF animation is lost through canvas
          // re-encoding, so both are passed through unchanged.
          return new File([file], generateUuidFileName(file.name), {
            type: file.type,
          });
        }

        const compressedBlob = await imageCompression(file, compressOptions);
        const extension =
          mimeToExtension[compressedBlob.type] ||
          file.name.split('.').pop().toLowerCase();
        const compressedFileName = generateUuidFileName(file.name).replace(
          /\.[^.]+$/,
          `.${extension}`,
        );

        return new File([compressedBlob], compressedFileName, {
          type: compressedBlob.type,
        });
      } catch {
        showErrorMessage('Compression error while reducing file size.');
      }
    };

    const compressAllFilesPromises = files.map(compressAllFiles);

    try {
      const results = await Promise.allSettled(compressAllFilesPromises);
      const formData = new FormData();
      let appendedCount = 0;

      results.forEach(({ status, value: compressedFile }) => {
        if (status === 'fulfilled' && compressedFile) {
          formData.append('files[]', compressedFile);
          appendedCount++;
        }
      });

      if (appendedCount === 0) {
        setIsUploadLoading(false);
        return;
      }

      formData.append('type', type);

      if (folderId) {
        formData.append('folderId', folderId);
      }

      await dispatch(uploadAssets({ payload: formData })).unwrap();

      showSuccessMessage(
        intl.formatMessage({ id: 'upload-folder-modal.success-message' }),
      );

      setPendingFiles([]);
      closeUploadFolderModal();
    } catch {
      // error toast is already shown by the thunk
    } finally {
      setIsUploadLoading(false);
    }
  };

  const handleCloseUploadFolderModal = () => {
    setPendingFiles([]);
    setUploadType('image');
    closeUploadFolderModal();
  };

  const handleDeleteImage = async (editor, name, type = 'image') => {
    try {
      setIsTemplateImageDeleteLoading(true);

      await dispatch(deleteAsset({ id, name, type })).then(
        ({ meta: { requestStatus } }) => {
          if (requestStatus === 'fulfilled') {
            const wrapper = editor.getWrapper();
            const assetUrl =
              type === 'icon'
                ? buildIconAssetsUrl(id, name)
                : buildAssetsUrl(id, name);
            const allImagesExist =
              wrapper?.findType(type === 'icon' ? 'icon-wrapper' : 'image') ||
              [];

            allImagesExist.forEach((img) => {
              const { src } = img.getAttributes();

              if (src === assetUrl) {
                img.remove();
              }
            });
          }
        },
      );
    } catch (error) {
      showErrorMessage(getErrorText(error));

      throw error;
    } finally {
      setIsTemplateImageDeleteLoading(false);
      closeConfirmTemplateDeleteModal();
    }
  };

  const handleDeleteGlobalImage = async (assetName, type = 'image') => {
    try {
      setIsGlobalImageDeleteLoading(true);

      await dispatch(deleteGlobalAsset({ assetName, type }));
    } catch (error) {
      showErrorMessage(getErrorText(error));

      throw error;
    } finally {
      setIsGlobalImageDeleteLoading(false);
      closeConfirmGlobalDeleteModal();
    }
  };

  return {
    isConfirmTemplateDeleteModalOpen,
    openConfirmTemplateDeleteModal,
    closeConfirmTemplateDeleteModal,
    isTemplateImageDeleteLoading,
    isConfirmGlobalDeleteModalOpen,
    openConfirmGlobalDeleteModal,
    closeConfirmGlobalDeleteModal,
    isGlobalImageDeleteLoading,
    isUploadFolderModalOpen,
    isUploadLoading,
    pendingFiles,
    templateImagesState,
    templateIconsState,
    globalImagesState,
    handleUploadImage,
    handleUploadWithFolder,
    handleCloseUploadFolderModal,
    handleDeleteImage,
    handleDeleteGlobalImage,
  };
};
